import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ModalController, Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { SelectionType } from "@swimlane/ngx-datatable";
import { Subscription } from "rxjs";
import { filter, map } from "rxjs/operators";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { iConnToolbarColour } from "src/components/shared-components/interfaces/iConnToolbarColour";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";

import { Environment } from "src/components/shared-components/class/general-classes/environment";
import {
  Column,
  ItemHistory,
  TableInfo,
} from "src/components/shared-components/class/general-classes/item-history";
import { EnvironmentType } from "src/components/shared-components/enum/enumEnvironment";
import { FiltersType } from "src/components/shared-components/enum/enumFilters";
import {
  ColumnTableType,
  HideShowColumn,
} from "src/components/shared-components/enum/enumTableHistory";
import { iGenericTable } from "src/model/interface/iGeneric-table";
import { EventService } from "src/service/event.service";
import { GatewayService } from "src/service/gateway.service";
import { LoaderService } from "src/service/loader.service";
import { LoggerService } from "src/service/logger.service";
import { NetworkService } from "src/service/network.service";
import { listEnvironment } from "src/state/selectors/environment.selectors";

@Component({
    selector: "app-environment-select",
    templateUrl: "./environment-select.component.html",
    styleUrls: ["./environment-select.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EnvironmentSelectComponent
  implements OnInit, OnDestroy, iConnToolbarColour, iEvent, iGenericTable {
  private _logContext: string = "Address-search.component";

  //#region Translation
  private _dictionary: string = "dictionary.";
  private _name: string = "name";
  //#endregion

  private _currentQuery: string = "";
  private _environmentsStoreSubscription: Subscription = null;

  public _eventSubscriptions: Array<Subscription> = null;
  public toolBarColor: string = "primary";
  public currentConnectionType: ConnectionType = ConnectionType.NONE;

  public tableInfo: TableInfo = null;
  public selected: Array<any> = new Array<any>();
  public listEnvironment: Array<Environment> = new Array<Environment>();

  constructor(
    private _modalCtrl: ModalController,
    private _gatewayService: GatewayService,

    private _platform: Platform,
    private _networkService: NetworkService,
    private _translateService: TranslateService,
    private _loaderService: LoaderService,
    private _loggerService: LoggerService,
    private _store: Store,
    private _eventService: EventService
  ) {
    this._gatewayService.Initialize();
  }

  public ngOnInit(): void {
    this.tableInfo = StaticClass.createTableInfo(
      "",
      false,
      SelectionType.multiClick,
      this.createArrayColumns(),
      this._platform.is("android") || this._platform.is("ios"),
      false
    );
    this.tableInfo.RowHeight = "auto";
    this.tableInfo.IsListInitialized = true;

    this.initFilterFields();
    this.subscribeEvents();
    this.loadListEnvironment();
    this.onClickFilter("");
  }

  /**
   *  Loa full list of environments base  on filter parameter
   */
  private loadListEnvironment(): void {
    this._environmentsStoreSubscription = this._store
      .select(listEnvironment)
      .pipe(
        filter(
          (listEnvironmentObject: GenericStoreApiData) =>
            listEnvironmentObject !== null && listEnvironmentObject !== undefined
        ),
        filter(
          (listEnvironmentObject: GenericStoreApiData) =>
            listEnvironmentObject.loaded
        ),
        filter(
          (listEnvironmentObject: GenericStoreApiData) =>
            !listEnvironmentObject.errorGeneric
        ),
        map(
          (listEnvironmentObject: GenericStoreApiData) =>
            listEnvironmentObject.data?.data || []
        ),
        map((listEnvironment: Array<Environment>) => {
          return listEnvironment.filter(
            (environment) =>
              environment.environmentType == EnvironmentType.ROOM ||
              environment.environmentType == EnvironmentType.SUITE ||
              environment.environmentType == EnvironmentType.CUSTOMIZED
          );
        })
      )
      .subscribe((listEnvironment: Array<Environment>) => {
        this.listEnvironment = listEnvironment;
        this.createItemHistoryDesktopList();
      });
  }

  public ngOnDestroy(): void {
    this.unsubscribeEvents();
  }

  //#region Subscribe to event-service observable
  public subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this._modalCtrl.dismiss();
      })
    );
  }

  public unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Receives the selected item in the table.
   * @param selected
   */
  public selectionChanged(selected: any): void {
    this.selected = selected;
  }

  /**
   * Creates the array containing the objects to show in the app view.
   */
  public async createItemHistoryList(): Promise<void> {
    this.tableInfo.List = [];
    for (let i = 0; i < this.listEnvironment.length; i++) {
      let newItem: ItemHistory = new ItemHistory();
      newItem.Id = this.listEnvironment[i].id;
      newItem.IconInfo = null;
      newItem.Title = StaticClass.createTextInfo(
        this.listEnvironment[i].descriptionEnvironment,
        ""
      );

      newItem.Subtitles = [];

      this.tableInfo.List.push(newItem);
    }
    await this._loaderService.dismissLoaderWaiting();
  }

  /**
   * Creates and returns the columns to pass to the table.
   */
  public createArrayColumns(): Column[] {
    let columns: Column[] = [];

    columns.push(
      StaticClass.createColumnObj(
        "descriptionEnvironment",
        this._name,
        "",
        ColumnTableType.STRING,
        HideShowColumn.SHOW
      )
    );

    return columns;
  }

  /**
   * Creates the table rows.
   */
  public async createItemHistoryDesktopList(): Promise<void> {
    this.tableInfo.List = [];
    if (this._currentQuery) {
      const searchValue = this._currentQuery.split("name=")[1];
      for (let i = 0; i < this.listEnvironment.length; i++) {
        const environment = this.listEnvironment[i];
        if (environment.descriptionEnvironment.includes(searchValue)) {
          this.tableInfo.List.push(environment);
        }
      }
    } else {
      this.tableInfo.List = StaticClass.createArrayForTable(
        this.listEnvironment,
        []
      );
    }

    this.selected = this.selected;
    await this._loaderService.dismissLoaderWaiting();
  }

  /**
   * Filter list and load row in the table
   */
  public async onClickFilter(query: string): Promise<void> {
    this._currentQuery = query;
    await this.createItemHistoryDesktopList();
  }

  /**
   * Exit without press confirm
   */
  public onClickCancel(): void {
    this._modalCtrl.dismiss();
  }

  /**
   * On confirm return the selected environments if they are selected
   */
  public onClickConfirm(): void {
    if (this.selected != null)
      this._modalCtrl.dismiss({ selectedEnvironment: this.selected });
    else this._modalCtrl.dismiss({ selectedEnvironment: null });
  }

  /**
   * Creates the array containing the filters to be used for queries.
   */
  private initFilterFields(): void {
    this.tableInfo.Filters = [];

    this.tableInfo.Filters.push(
      StaticClass.createFilterObject(
        FiltersType.text,
        this._name,
        this._dictionary + this._name,
        "",
        [],
        ""
      )
    );
  }
}
